import {
  DEFAULT_VIEWER_RENDER_ORDER_STRATEGY,
  DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE,
  DEFAULT_VIEWER_GEOMETRY_FORMAT,
  DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT,
  DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT,
  defaultViewerRuntimeSettings,
  formatViewerRuntimeUrlValue,
  parseViewerRuntimeFieldValue,
  validateViewerRuntimeSettings,
  VIEWER_GEOMETRY_FORMATS,
  VIEWER_FRUSTUM_RESPONSE_FORMATS,
  ViewerFrustumResponseFormat,
  VIEWER_RUNTIME_FIELDS,
  ViewerGeometryFormat,
  VIEWER_RENDER_ORDER_STRATEGIES,
  ViewerRenderOrderStrategy,
  DEFAULT_VIEWER_FRUSTUM_SORT_ORDER,
  VIEWER_FRUSTUM_SORT_ORDERS,
  ViewerFrustumSortOrder,
  ViewerRuntimeSettings,
  ViewerNavigationMinScreenSize,
  VIEWER_POINT_CLOUD_GEOMETRY_FORMATS,
  ViewerPointCloudGeometryFormat,
} from './viewer-runtime-settings';
import {
  DEFAULT_VIEWER_VISUAL_QUALITY_PROFILE,
  defaultViewerVisualQualitySettings,
  validateViewerVisualQualitySettings,
  viewerVisualQualityProfileDefaults,
  VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS,
  VIEWER_VISUAL_QUALITY_PROFILES,
  ViewerVisualQualityAntiAliasing,
  ViewerVisualQualityProfile,
  ViewerVisualQualityShadingMode,
  ViewerVisualQualitySettings,
} from './viewer-visual-quality';

function isViewerPointCloudGeometryFormat(value: string): value is ViewerPointCloudGeometryFormat {
  return VIEWER_POINT_CLOUD_GEOMETRY_FORMATS.some(
    (format: (typeof VIEWER_POINT_CLOUD_GEOMETRY_FORMATS)[number]) => format.value === value
  );
}

function isViewerRenderOrderStrategy(value: string): value is ViewerRenderOrderStrategy {
  return VIEWER_RENDER_ORDER_STRATEGIES.some(
    (strategy: (typeof VIEWER_RENDER_ORDER_STRATEGIES)[number]) => strategy.value === value
  );
}

function isViewerFrustumSortOrder(value: string): value is ViewerFrustumSortOrder {
  return VIEWER_FRUSTUM_SORT_ORDERS.some(
    (sortOrder: (typeof VIEWER_FRUSTUM_SORT_ORDERS)[number]) => sortOrder.value === value
  );
}

function isViewerGeometryFormat(value: string): value is ViewerGeometryFormat {
  return VIEWER_GEOMETRY_FORMATS.some((format: (typeof VIEWER_GEOMETRY_FORMATS)[number]) => format.value === value);
}

function isViewerFrustumResponseFormat(value: string): value is ViewerFrustumResponseFormat {
  return VIEWER_FRUSTUM_RESPONSE_FORMATS.some(
    (format: (typeof VIEWER_FRUSTUM_RESPONSE_FORMATS)[number]) => format.value === value
  );
}

type CadBooleanKey = 'ssaoEnabled' | 'edgesEnabled' | 'tonemapEnabled' | 'gradientBackground' | 'vignette';
type CadNumericKey =
  | 'ssaoIntensity'
  | 'ssaoRadius'
  | 'edgeIntensity'
  | 'edgeWidth'
  | 'tonemapStrength'
  | 'saturation'
  | 'warmth'
  | 'keyLight'
  | 'sharpen';

const CAD_URL_BOOLEANS: Record<CadBooleanKey, string> = {
  ssaoEnabled: 'cadSsao',
  edgesEnabled: 'cadEdges',
  tonemapEnabled: 'cadTonemap',
  gradientBackground: 'cadGradient',
  vignette: 'cadVignette',
};

const CAD_URL_NUMBERS: Record<CadNumericKey, string> = {
  ssaoIntensity: 'cadSsaoIntensity',
  ssaoRadius: 'cadSsaoRadius',
  edgeIntensity: 'cadEdgeIntensity',
  edgeWidth: 'cadEdgeWidth',
  tonemapStrength: 'cadTonemapStrength',
  saturation: 'cadSaturation',
  warmth: 'cadWarmth',
  keyLight: 'cadKeyLight',
  sharpen: 'cadSharpen',
};

function isViewerVisualQualityProfile(value: string): value is ViewerVisualQualityProfile {
  return VIEWER_VISUAL_QUALITY_PROFILES.some(
    (profile: (typeof VIEWER_VISUAL_QUALITY_PROFILES)[number]) => profile.value === value
  );
}

function isViewerVisualQualityAntiAliasing(value: string): value is ViewerVisualQualityAntiAliasing {
  return VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS.some(
    (option: (typeof VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS)[number]) => option.value === value
  );
}

function isViewerVisualQualityShadingMode(value: string): value is ViewerVisualQualityShadingMode {
  return value === 'unlit' || value === 'diffuse';
}

export type ViewerCameraVector = [number, number, number];

export interface ViewerCameraView {
  position: ViewerCameraVector;
  direction: ViewerCameraVector;
}

export function decodeCameraView(raw: string | null): ViewerCameraView | null {
  if (raw === null) {
    return null;
  }
  try {
    if (!/^[A-Za-z0-9_-]+$/.test(raw)) {
      return null;
    }
    const padded: string = raw.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (raw.length % 4)) % 4);
    const binary: string = atob(padded);
    if (binary.length !== 48) {
      return null;
    }
    const dataView: DataView = new DataView(new ArrayBuffer(binary.length));
    const bytes: Uint8Array = new Uint8Array(dataView.buffer);
    for (let index: number = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    const values: number[] = Array.from({ length: 6 }, (_value: unknown, index: number) =>
      dataView.getFloat64(index * 8, true)
    );
    if (!values.every(Number.isFinite) || Math.hypot(values[3], values[4], values[5]) === 0) {
      return null;
    }
    return {
      position: [values[0], values[1], values[2]],
      direction: [values[3], values[4], values[5]],
    };
  } catch {
    return null;
  }
}

export function encodeCameraView(cameraView: ViewerCameraView | undefined): string | null {
  if (cameraView === undefined) {
    return null;
  }
  const dataView: DataView = new DataView(new ArrayBuffer(48));
  [...cameraView.position, ...cameraView.direction].forEach((value: number, index: number) => {
    dataView.setFloat64(index * 8, value, true);
  });
  let binary: string = '';
  const bytes: Uint8Array = new Uint8Array(dataView.buffer);
  bytes.forEach((value: number) => {
    binary += String.fromCharCode(value);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

// eslint-disable-next-line complexity
export function readViewerUrlSettings(params: { get: (key: string) => string | null }): {
  settings: ViewerRuntimeSettings;
  downloadBatchAuto: boolean;
  distributedRendering: boolean;
  navigationMinScreenSize: ViewerNavigationMinScreenSize;
  renderOrderStrategy: ViewerRenderOrderStrategy;
  meshFrustumSortOrder: ViewerFrustumSortOrder;
  geometryFormat: ViewerGeometryFormat;
  pointCloudGeometryFormat: ViewerPointCloudGeometryFormat;
  frustumResponseFormat: ViewerFrustumResponseFormat;
  disableTransparentMaterials: boolean;
  enableInstancing: boolean;
  pointCloudAdaptivePointSize: boolean;
  hlodOcclusionRefinement: boolean;
  freezeFrustum: boolean;
  showPointCloudBoxes: boolean;
  showObjectBoundingBoxes: boolean;
  showViewerLoading: boolean;
  useCompactFrustum: boolean;
  viewerProfilingEnabled: boolean;
  viewerDeepProfilingEnabled: boolean;
  visualQuality: ViewerVisualQualitySettings;
  cameraView: ViewerCameraView | null;
  viewpointId: string | null;
  error: string | null;
} {
  const settings: ViewerRuntimeSettings = defaultViewerRuntimeSettings();
  for (const field of VIEWER_RUNTIME_FIELDS) {
    const raw: string | null = params.get(field.key);
    if (raw !== null) {
      settings[field.key] = parseViewerRuntimeFieldValue(field, raw, true);
    }
  }
  const rawDownloadBatchAuto: string | null = params.get('downloadBatchAuto');
  const rawCamera: string | null = params.get('camera');
  const cameraView: ViewerCameraView | null = decodeCameraView(rawCamera);
  const hasInvalidCamera: boolean = rawCamera !== null && cameraView === null;
  const rawViewpointId: string | null = params.get('viewpointId');
  const trimmedViewpointId: string = rawViewpointId?.trim() ?? '';
  const viewpointId: string | null = trimmedViewpointId.length > 0 ? trimmedViewpointId : null;
  const hasInvalidViewpointId: boolean = rawViewpointId !== null && viewpointId === null;
  const distributed: string | null = params.get('distributedRendering');
  const rawNavigationMinScreenSize: string | null = params.get('navigationMinScreenSize');
  const navigationMinScreenSize: ViewerNavigationMinScreenSize =
    rawNavigationMinScreenSize === 'off'
      ? 0
      : rawNavigationMinScreenSize === '8'
      ? 8
      : rawNavigationMinScreenSize === '32'
      ? 32
      : DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE;
  const hasInvalidNavigationMinScreenSize: boolean =
    rawNavigationMinScreenSize !== null && !['off', '8', '16', '32'].includes(rawNavigationMinScreenSize);
  const rawRenderOrderStrategy: string | null = params.get('renderOrderStrategy');
  const rawMeshFrustumSortOrder: string | null = params.get('meshFrustumSortOrder');
  const rawGeometryFormat: string | null = params.get('geometryFormat');
  const rawPointCloudGeometryFormat: string | null = params.get('pointCloudGeometryFormat');
  const rawFrustumResponseFormat: string | null = params.get('frustumResponseFormat');
  const disableTransparentMaterials: string | null = params.get('disableTransparentMaterials');
  const enableInstancing: string | null = params.get('enableInstancing');
  const pointCloudAdaptivePointSize: string | null = params.get('pointCloudAdaptivePointSize');
  const hlodOcclusionRefinement: string | null = params.get('hlodOcclusionRefinement');
  const freezeFrustum: string | null = params.get('freezeFrustum');
  const showPointCloudBoxes: string | null = params.get('showPointCloudBoxes');
  const showObjectBoundingBoxes: string | null = params.get('showObjectBoundingBoxes');
  const showViewerLoading: string | null = params.get('showViewerLoading');
  const compactFrustum: string | null = params.get('compactFrustum');
  const viewerProfilingEnabled: string | null = params.get('viewerProfiling');
  const viewerDeepProfilingEnabled: string | null = params.get('viewerDeepProfiling');
  const rawVisualQualityProfile: string | null = params.get('qualityProfile');
  const rawAntiAliasing: string | null = params.get('qualityAntiAliasing');
  const rawShadingMode: string | null = params.get('qualityShadingMode');
  const visualQualityProfile: ViewerVisualQualityProfile =
    rawVisualQualityProfile !== null && isViewerVisualQualityProfile(rawVisualQualityProfile)
      ? rawVisualQualityProfile
      : DEFAULT_VIEWER_VISUAL_QUALITY_PROFILE;
  const visualQuality: ViewerVisualQualitySettings = viewerVisualQualityProfileDefaults(visualQualityProfile);
  const visualQualityNumericFields: (
    | 'ambientIntensity'
    | 'directionalIntensity'
    | 'specularIntensity'
    | 'fresnelIntensity'
    | 'exposure'
    | 'contrast'
  )[] = ['ambientIntensity', 'directionalIntensity', 'specularIntensity', 'fresnelIntensity', 'exposure', 'contrast'];
  visualQualityNumericFields.forEach((field: (typeof visualQualityNumericFields)[number]) => {
    const raw: string | null = params.get(`quality${field[0].toUpperCase()}${field.slice(1)}`);
    if (raw !== null) {
      visualQuality[field] = raw.trim() === '' ? NaN : Number(raw);
    }
  });
  const visualQualityBooleanFields: ('specularEnabled' | 'fresnelEnabled')[] = ['specularEnabled', 'fresnelEnabled'];
  visualQualityBooleanFields.forEach((field: (typeof visualQualityBooleanFields)[number]) => {
    const raw: string | null = params.get(`quality${field[0].toUpperCase()}${field.slice(1)}`);
    if (raw !== null) {
      visualQuality[field] = raw === 'true';
    }
  });
  const cadBooleanKeys: CadBooleanKey[] = Object.keys(CAD_URL_BOOLEANS) as CadBooleanKey[];
  cadBooleanKeys.forEach((field: CadBooleanKey) => {
    const raw: string | null = params.get(CAD_URL_BOOLEANS[field]);
    if (raw !== null) {
      visualQuality.cad[field] = raw === 'true';
    }
  });
  const cadNumericKeys: CadNumericKey[] = Object.keys(CAD_URL_NUMBERS) as CadNumericKey[];
  cadNumericKeys.forEach((field: CadNumericKey) => {
    const raw: string | null = params.get(CAD_URL_NUMBERS[field]);
    if (raw !== null) {
      visualQuality.cad[field] = raw.trim() === '' ? NaN : Number(raw);
    }
  });
  if (rawAntiAliasing !== null && isViewerVisualQualityAntiAliasing(rawAntiAliasing)) {
    visualQuality.antiAliasing = rawAntiAliasing;
  }
  if (rawShadingMode !== null && isViewerVisualQualityShadingMode(rawShadingMode)) {
    visualQuality.shadingMode = rawShadingMode;
  }
  const hasInvalidTransparentMaterialsSetting: boolean =
    disableTransparentMaterials !== null &&
    disableTransparentMaterials !== 'true' &&
    disableTransparentMaterials !== 'false';
  const hasInvalidInstancingSetting: boolean =
    enableInstancing !== null && enableInstancing !== 'true' && enableInstancing !== 'false';
  const hasInvalidAdaptivePointSizeSetting: boolean =
    pointCloudAdaptivePointSize !== null &&
    pointCloudAdaptivePointSize !== 'true' &&
    pointCloudAdaptivePointSize !== 'false';
  const hasInvalidOcclusionRefinementSetting: boolean =
    hlodOcclusionRefinement !== null && hlodOcclusionRefinement !== 'true' && hlodOcclusionRefinement !== 'false';
  const hasInvalidFreezeFrustumSetting: boolean =
    freezeFrustum !== null && freezeFrustum !== 'true' && freezeFrustum !== 'false';
  const hasInvalidPointCloudBoxesSetting: boolean =
    showPointCloudBoxes !== null && showPointCloudBoxes !== 'true' && showPointCloudBoxes !== 'false';
  const hasInvalidObjectBoundingBoxesSetting: boolean =
    showObjectBoundingBoxes !== null && showObjectBoundingBoxes !== 'true' && showObjectBoundingBoxes !== 'false';
  const hasInvalidViewerLoadingSetting: boolean =
    showViewerLoading !== null && showViewerLoading !== 'true' && showViewerLoading !== 'false';
  const hasInvalidCompactFrustumSetting: boolean =
    compactFrustum !== null && compactFrustum !== 'true' && compactFrustum !== 'false';
  const hasInvalidViewerProfilingSetting: boolean =
    viewerProfilingEnabled !== null && viewerProfilingEnabled !== 'true' && viewerProfilingEnabled !== 'false';
  const hasInvalidDeepViewerProfilingSetting: boolean =
    viewerDeepProfilingEnabled !== null &&
    viewerDeepProfilingEnabled !== 'true' &&
    viewerDeepProfilingEnabled !== 'false';
  const hasInvalidVisualQualityProfile: boolean =
    rawVisualQualityProfile !== null && !isViewerVisualQualityProfile(rawVisualQualityProfile);
  const hasInvalidVisualQualityAntiAliasing: boolean =
    rawAntiAliasing !== null && !isViewerVisualQualityAntiAliasing(rawAntiAliasing);
  const hasInvalidVisualQualityShadingMode: boolean =
    rawShadingMode !== null && !isViewerVisualQualityShadingMode(rawShadingMode);
  const hasInvalidVisualQualityBoolean: boolean =
    visualQualityBooleanFields.some((field: (typeof visualQualityBooleanFields)[number]) => {
      const raw: string | null = params.get(`quality${field[0].toUpperCase()}${field.slice(1)}`);
      return raw !== null && raw !== 'true' && raw !== 'false';
    }) ||
    cadBooleanKeys.some((field: CadBooleanKey) => {
      const raw: string | null = params.get(CAD_URL_BOOLEANS[field]);
      return raw !== null && raw !== 'true' && raw !== 'false';
    });
  const renderOrderStrategy: ViewerRenderOrderStrategy =
    rawRenderOrderStrategy !== null && isViewerRenderOrderStrategy(rawRenderOrderStrategy)
      ? rawRenderOrderStrategy
      : DEFAULT_VIEWER_RENDER_ORDER_STRATEGY;
  const meshFrustumSortOrder: ViewerFrustumSortOrder =
    rawMeshFrustumSortOrder !== null && isViewerFrustumSortOrder(rawMeshFrustumSortOrder)
      ? rawMeshFrustumSortOrder
      : DEFAULT_VIEWER_FRUSTUM_SORT_ORDER;
  const geometryFormat: ViewerGeometryFormat =
    rawGeometryFormat !== null && isViewerGeometryFormat(rawGeometryFormat)
      ? rawGeometryFormat
      : DEFAULT_VIEWER_GEOMETRY_FORMAT;
  const pointCloudGeometryFormat: ViewerPointCloudGeometryFormat =
    rawPointCloudGeometryFormat !== null && isViewerPointCloudGeometryFormat(rawPointCloudGeometryFormat)
      ? rawPointCloudGeometryFormat
      : DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT;
  const frustumResponseFormat: ViewerFrustumResponseFormat =
    rawFrustumResponseFormat !== null && isViewerFrustumResponseFormat(rawFrustumResponseFormat)
      ? rawFrustumResponseFormat
      : DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT;
  const error: string | null =
    validateViewerRuntimeSettings(settings) ??
    (distributed !== null && distributed !== 'true' && distributed !== 'false'
      ? 'distributedRendering: use true or false.'
      : rawDownloadBatchAuto !== null && rawDownloadBatchAuto !== 'true' && rawDownloadBatchAuto !== 'false'
      ? 'downloadBatchAuto: use true or false.'
      : hasInvalidNavigationMinScreenSize
      ? 'navigationMinScreenSize: use off, 8, 16, or 32.'
      : hasInvalidTransparentMaterialsSetting
      ? 'disableTransparentMaterials: use true or false.'
      : hasInvalidInstancingSetting
      ? 'enableInstancing: use true or false.'
      : hasInvalidAdaptivePointSizeSetting
      ? 'pointCloudAdaptivePointSize: use true or false.'
      : hasInvalidOcclusionRefinementSetting
      ? 'hlodOcclusionRefinement: use true or false.'
      : hasInvalidFreezeFrustumSetting
      ? 'freezeFrustum: use true or false.'
      : hasInvalidPointCloudBoxesSetting
      ? 'showPointCloudBoxes: use true or false.'
      : hasInvalidObjectBoundingBoxesSetting
      ? 'showObjectBoundingBoxes: use true or false.'
      : hasInvalidViewerLoadingSetting
      ? 'showViewerLoading: use true or false.'
      : hasInvalidCompactFrustumSetting
      ? 'compactFrustum: use true or false.'
      : hasInvalidViewerProfilingSetting
      ? 'viewerProfiling: use true or false.'
      : hasInvalidDeepViewerProfilingSetting
      ? 'viewerDeepProfiling: use true or false.'
      : hasInvalidVisualQualityProfile
      ? `qualityProfile: use ${VIEWER_VISUAL_QUALITY_PROFILES.map(
          (profile: (typeof VIEWER_VISUAL_QUALITY_PROFILES)[number]) => profile.value
        ).join(', ')}.`
      : hasInvalidVisualQualityAntiAliasing
      ? 'qualityAntiAliasing: use off, msaa2, msaa4, or msaa8.'
      : hasInvalidVisualQualityShadingMode
      ? 'qualityShadingMode: use unlit or diffuse.'
      : hasInvalidVisualQualityBoolean
      ? 'quality effect toggles: use true or false.'
      : rawRenderOrderStrategy !== null && !isViewerRenderOrderStrategy(rawRenderOrderStrategy)
      ? 'renderOrderStrategy: use one of the supported strategies.'
      : rawMeshFrustumSortOrder !== null && !isViewerFrustumSortOrder(rawMeshFrustumSortOrder)
      ? 'meshFrustumSortOrder: use distance or angular-size.'
      : rawGeometryFormat !== null && !isViewerGeometryFormat(rawGeometryFormat)
      ? 'geometryFormat: use binary or draco.'
      : rawPointCloudGeometryFormat !== null && !isViewerPointCloudGeometryFormat(rawPointCloudGeometryFormat)
      ? 'pointCloudGeometryFormat: use binary, draco, quantized or quantized2.'
      : rawFrustumResponseFormat !== null && !isViewerFrustumResponseFormat(rawFrustumResponseFormat)
      ? 'frustumResponseFormat: use binary or json.'
      : hasInvalidCamera
      ? 'camera: use the compact URL-safe Base64 camera value.'
      : hasInvalidViewpointId
      ? 'viewpointId: use a non-empty viewpoint identifier.'
      : validateViewerVisualQualitySettings(visualQuality));
  return {
    settings,
    downloadBatchAuto: rawDownloadBatchAuto !== 'false',
    distributedRendering: distributed !== 'false',
    navigationMinScreenSize,
    renderOrderStrategy,
    meshFrustumSortOrder,
    geometryFormat,
    pointCloudGeometryFormat,
    frustumResponseFormat,
    disableTransparentMaterials: disableTransparentMaterials === 'true',
    enableInstancing: enableInstancing !== 'false',
    pointCloudAdaptivePointSize: pointCloudAdaptivePointSize !== 'false',
    hlodOcclusionRefinement: hlodOcclusionRefinement !== 'false',
    freezeFrustum: freezeFrustum === 'true',
    showPointCloudBoxes: showPointCloudBoxes === 'true',
    showObjectBoundingBoxes: showObjectBoundingBoxes === 'true',
    showViewerLoading: showViewerLoading !== 'false',
    useCompactFrustum: compactFrustum !== 'false',
    viewerProfilingEnabled: viewerProfilingEnabled === 'true',
    viewerDeepProfilingEnabled: viewerProfilingEnabled === 'true' && viewerDeepProfilingEnabled === 'true',
    visualQuality,
    cameraView,
    viewpointId,
    error,
  };
}

export function writeViewerUrlSettings(
  settings: ViewerRuntimeSettings,
  distributedRendering: boolean,
  renderOrderStrategy: ViewerRenderOrderStrategy = DEFAULT_VIEWER_RENDER_ORDER_STRATEGY,
  disableTransparentMaterials: boolean = false,
  freezeFrustum: boolean = false,
  showPointCloudBoxes: boolean = false,
  enableInstancing: boolean = true,
  showViewerLoading: boolean = true,
  viewerProfilingEnabled: boolean = false,
  viewerDeepProfilingEnabled: boolean = false,
  useCompactFrustum: boolean = true,
  geometryFormat: ViewerGeometryFormat = DEFAULT_VIEWER_GEOMETRY_FORMAT,
  visualQuality: ViewerVisualQualitySettings = defaultViewerVisualQualitySettings(),
  pointCloudGeometryFormat: ViewerPointCloudGeometryFormat = DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT,
  showObjectBoundingBoxes: boolean = false,
  meshFrustumSortOrder: ViewerFrustumSortOrder = DEFAULT_VIEWER_FRUSTUM_SORT_ORDER,
  cameraView?: ViewerCameraView,
  viewpointId?: string | null,
  navigationMinScreenSize: ViewerNavigationMinScreenSize = DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE,
  downloadBatchAuto: boolean = true,
  frustumResponseFormat: ViewerFrustumResponseFormat = DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT,
  pointCloudAdaptivePointSize: boolean = true,
  hlodOcclusionRefinement: boolean = true
): Record<string, string | null> {
  const queryParams: Record<string, string | null> = {};
  // Defaults can depend on the device (the photogrammetry cache), so compare with this device's.
  const defaults: ViewerRuntimeSettings = defaultViewerRuntimeSettings();
  VIEWER_RUNTIME_FIELDS.forEach((field: (typeof VIEWER_RUNTIME_FIELDS)[number]) => {
    queryParams[field.key] =
      settings[field.key] === defaults[field.key] ? null : formatViewerRuntimeUrlValue(field, settings[field.key]);
  });
  queryParams.distributedRendering = distributedRendering ? null : 'false';
  queryParams.downloadBatchAuto = downloadBatchAuto ? null : 'false';
  queryParams.navigationMinScreenSize =
    navigationMinScreenSize === DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE
      ? null
      : navigationMinScreenSize === 0
      ? 'off'
      : String(navigationMinScreenSize);
  queryParams.renderOrderStrategy =
    renderOrderStrategy === DEFAULT_VIEWER_RENDER_ORDER_STRATEGY ? null : renderOrderStrategy;
  queryParams.meshFrustumSortOrder =
    meshFrustumSortOrder === DEFAULT_VIEWER_FRUSTUM_SORT_ORDER ? null : meshFrustumSortOrder;
  queryParams.geometryFormat = geometryFormat === DEFAULT_VIEWER_GEOMETRY_FORMAT ? null : geometryFormat;
  queryParams.pointCloudGeometryFormat =
    pointCloudGeometryFormat === DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT ? null : pointCloudGeometryFormat;
  queryParams.frustumResponseFormat =
    frustumResponseFormat === DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT ? null : frustumResponseFormat;
  queryParams.disableTransparentMaterials = disableTransparentMaterials ? 'true' : null;
  queryParams.enableInstancing = enableInstancing ? null : 'false';
  queryParams.pointCloudAdaptivePointSize = pointCloudAdaptivePointSize ? null : 'false';
  queryParams.hlodOcclusionRefinement = hlodOcclusionRefinement ? null : 'false';
  queryParams.freezeFrustum = freezeFrustum ? 'true' : null;
  queryParams.showPointCloudBoxes = showPointCloudBoxes ? 'true' : null;
  queryParams.showObjectBoundingBoxes = showObjectBoundingBoxes ? 'true' : null;
  queryParams.showViewerLoading = showViewerLoading ? null : 'false';
  queryParams.compactFrustum = useCompactFrustum ? null : 'false';
  queryParams.viewerProfiling = viewerProfilingEnabled ? 'true' : null;
  queryParams.viewerDeepProfiling = viewerProfilingEnabled && viewerDeepProfilingEnabled ? 'true' : null;
  const profileDefaults: ViewerVisualQualitySettings = viewerVisualQualityProfileDefaults(visualQuality.profile);
  queryParams.qualityProfile =
    visualQuality.profile === DEFAULT_VIEWER_VISUAL_QUALITY_PROFILE ? null : visualQuality.profile;
  (
    [
      'ambientIntensity',
      'directionalIntensity',
      'specularIntensity',
      'fresnelIntensity',
      'exposure',
      'contrast',
    ] as const
  ).forEach(
    (
      field:
        | 'ambientIntensity'
        | 'directionalIntensity'
        | 'specularIntensity'
        | 'fresnelIntensity'
        | 'exposure'
        | 'contrast'
    ) => {
      const key: string = `quality${field[0].toUpperCase()}${field.slice(1)}`;
      queryParams[key] = visualQuality[field] === profileDefaults[field] ? null : String(visualQuality[field]);
    }
  );
  (['specularEnabled', 'fresnelEnabled'] as const).forEach((field: 'specularEnabled' | 'fresnelEnabled') => {
    const key: string = `quality${field[0].toUpperCase()}${field.slice(1)}`;
    queryParams[key] = visualQuality[field] === profileDefaults[field] ? null : String(visualQuality[field]);
  });
  (Object.keys(CAD_URL_BOOLEANS) as CadBooleanKey[]).forEach((field: CadBooleanKey) => {
    queryParams[CAD_URL_BOOLEANS[field]] =
      visualQuality.cad[field] === profileDefaults.cad[field] ? null : String(visualQuality.cad[field]);
  });
  (Object.keys(CAD_URL_NUMBERS) as CadNumericKey[]).forEach((field: CadNumericKey) => {
    queryParams[CAD_URL_NUMBERS[field]] =
      visualQuality.cad[field] === profileDefaults.cad[field] ? null : String(visualQuality.cad[field]);
  });
  queryParams.qualityAntiAliasing =
    visualQuality.antiAliasing === profileDefaults.antiAliasing ? null : visualQuality.antiAliasing;
  queryParams.qualityShadingMode =
    visualQuality.shadingMode === profileDefaults.shadingMode ? null : visualQuality.shadingMode;
  queryParams.camera = encodeCameraView(cameraView);
  const trimmedViewpointId: string = viewpointId?.trim() ?? '';
  queryParams.viewpointId = trimmedViewpointId.length > 0 ? trimmedViewpointId : null;
  return queryParams;
}
